import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Renders children into a separate browser popup window via React portal.
 * The component stays in the same React tree, so all state/props remain connected.
 *
 * Optional **parent portal**: pass `onMountTargetReady` to receive the popup body container
 * and render the same UI with `createPortal` from the parent (single long-lived subtree).
 * In that mode `children` is ignored.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {() => void} props.onClose - Called when the popup is closed
 * @param {string} [props.title]
 * @param {number} [props.width=1200]
 * @param {number} [props.height=800]
 * @param {(el: HTMLElement | null) => void} [props.onMountTargetReady]
 */
export default function PopoutWindow({
  children,
  onClose,
  title = '3D Viewer',
  width = 1200,
  height = 800,
  onMountTargetReady,
}) {
  const [containerEl, setContainerEl] = useState(null);
  const windowRef = useRef(null);
  const closedByUs = useRef(false);

  useEffect(() => {
    const left = Math.round(window.screenX + (window.outerWidth - width) / 2);
    const top = Math.round(window.screenY + (window.outerHeight - height) / 2);

    const popup = window.open(
      '',
      '',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no`
    );

    if (!popup) {
      onClose();
      return;
    }

    popup.document.title = title;

    // Clone all stylesheets from parent window into the popup
    const parentStyles = document.querySelectorAll('style, link[rel="stylesheet"]');
    parentStyles.forEach(node => {
      const clone = node.cloneNode(true);
      if (clone.tagName === 'LINK') {
        clone.href = node.href;
      }
      popup.document.head.appendChild(clone);
    });

    popup.document.body.style.margin = '0';
    popup.document.body.style.padding = '0';
    popup.document.body.style.background = '#030712';
    popup.document.body.style.overflow = 'hidden';

    const container = popup.document.createElement('div');
    container.id = 'popout-root';
    container.style.cssText = 'width: 100%; height: 100vh; display: flex; flex-direction: column;';
    popup.document.body.appendChild(container);

    windowRef.current = popup;
    setContainerEl(container);

    const handleUnload = () => {
      if (!closedByUs.current) {
        onClose();
      }
    };
    popup.addEventListener('beforeunload', handleUnload);

    return () => {
      closedByUs.current = true;
      popup.removeEventListener('beforeunload', handleUnload);
      if (!popup.closed) {
        popup.close();
      }
      windowRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!onMountTargetReady) return undefined;
    if (containerEl) {
      onMountTargetReady(containerEl);
      return () => onMountTargetReady(null);
    }
    return undefined;
  }, [containerEl, onMountTargetReady]);

  if (onMountTargetReady) {
    return null;
  }

  return containerEl ? createPortal(children, containerEl) : null;
}
